// @givmo/fundraisers-module — the Messages tab's reads and refusals, as FCM-BE84 serves them (FCM-WEB50;
// plan §FC-17.5), over the generated `/org/{org_ref}/fundraisers/{fundraiser_id}/donor-messages` ops.
//
// ⚠ EVERY FACT IS THE SERVER'S. Who a message reaches is a count the server fixes, the preview is its own
// render of the email, and a refusal is its own sentence (D-FCM-60's ruled words). Nothing here decides
// who gets a message or why one is refused: this file only reads what the server answered, fails closed
// where a read would otherwise claim something it did not say, and says WHERE each refusal shows.

import {
  extractList,
  firstErrorCode,
  firstErrorContext,
  isGivmoError,
  type OrganizerMessageHistoryRowOut,
  type OrganizerMessagePreviewOut,
} from '@givmo/api-client';

/** Rows per history page. The read asks for one more, the next-page sentinel: `meta` never arrives. */
export const MESSAGES_PAGE_SIZE = 25;

/**
 * The send's request model caps the raw body at 10,000 characters (`OrganizerMessageSendIn.body`) before
 * the guard ever reads it, and answers past it with a validation error that is no ruled sentence. The box
 * holds the text to it (`maxLength`, which counts UTF-16 units, so never more characters than this), and
 * every longer message still gets the guard's own words: "A message can be 1 to 2,000 characters."
 */
export const MESSAGE_BODY_LIMIT = 10_000;

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}
const textOr = (value: unknown, fallback: string) => (typeof value === 'string' ? value : fallback);

/**
 * Whether the `/org` detail lights the tab: `organizer_tools.messages` (FCM-BE84), present only while
 * messages serve for this fundraiser and the caller may send (the module's OWNER tier). Read defensively
 * although the snapshot types it: only a literal `true` lights it.
 */
export function messagesServed(row: unknown): boolean {
  return isRecord(row) && isRecord(row.organizer_tools) && row.organizer_tools.messages === true;
}

/**
 * The whitespace Python's `str.strip()` trims (every character `str.isspace()` names), which differs from
 * JavaScript's `trim()`: Python also trims U+001C–U+001F and U+0085, and keeps U+FEFF.
 */
const SERVER_SPACE = new Set([
  0x09, 0x0a, 0x0b, 0x0c, 0x0d, 0x1c, 0x1d, 0x1e, 0x1f, 0x20, 0x85, 0xa0, 0x1680, 0x2000, 0x2001,
  0x2002, 0x2003, 0x2004, 0x2005, 0x2006, 0x2007, 0x2008, 0x2009, 0x200a, 0x2028, 0x2029, 0x202f,
  0x205f, 0x3000,
]);

/**
 * A message's length as the server's guard counts it (`normalize_message`, D-FCM-60's cap): its line
 * breaks read as LF, its ends trimmed as Python trims them, then counted in characters (code points, so
 * an emoji counts once, where a JavaScript `length` counts it twice).
 */
export function messageLength(value: string): number {
  const characters = [...value.replace(/\r\n?/g, '\n')];
  let start = 0;
  let end = characters.length;
  while (start < end && SERVER_SPACE.has(characters[start]!.codePointAt(0)!)) start += 1;
  while (end > start && SERVER_SPACE.has(characters[end - 1]!.codePointAt(0)!)) end -= 1;
  return end - start;
}

/**
 * A history page's rows off the unwrapped list. A body that is not a list fails the read, never an empty
 * page (an empty history would be a claim about a read that did not answer), and so does a row missing
 * what it is keyed, dated and counted by: dropping it would claim a shorter history and move the
 * next-page sentinel. `removed_by_givmo` is true only for a literal `true`.
 */
export function requireMessageRows(body: unknown): OrganizerMessageHistoryRowOut[] {
  if (!Array.isArray(body) && !(isRecord(body) && Array.isArray(body.data))) {
    throw new Error('The message history could not be read.');
  }
  return extractList<unknown>(body).map((row): OrganizerMessageHistoryRowOut => {
    if (
      !isRecord(row) ||
      typeof row.id !== 'string' ||
      typeof row.created_at !== 'string' ||
      typeof row.recipient_count !== 'number' ||
      !Number.isInteger(row.recipient_count)
    ) {
      throw new Error('The message history could not be read.');
    }
    return {
      id: row.id,
      created_at: row.created_at,
      recipient_count: row.recipient_count,
      signed_name: textOr(row.signed_name, ''),
      body: textOr(row.body, ''),
      removed_by_givmo: row.removed_by_givmo === true,
    };
  });
}

/** The dry run's answer: how many donors the message goes to now, and the server's render of it. */
export interface MessagePreview {
  recipientCount: number;
  preview: OrganizerMessagePreviewOut;
}

/**
 * The dry run's answer, `{dry_run: true, recipient_count, preview}`, or null when it is anything else. The
 * email itself (`email_html`) is what the preview shows, so an answer without it is no preview.
 */
export function decodePreview(body: unknown): MessagePreview | null {
  if (!isRecord(body) || body.dry_run !== true) return null;
  const count = body.recipient_count;
  const preview = body.preview;
  if (typeof count !== 'number' || !Number.isInteger(count) || !isRecord(preview)) return null;
  const html = preview.email_html;
  if (typeof html !== 'string' || html.trim() === '') return null;
  return {
    recipientCount: count,
    preview: {
      subject: textOr(preview.subject, ''),
      from_name: textOr(preview.from_name, ''),
      signed_name: textOr(preview.signed_name, ''),
      fundraiser_name: textOr(preview.fundraiser_name, ''),
      body: textOr(preview.body, ''),
      framing: textOr(preview.framing, ''),
      notes: Array.isArray(preview.notes)
        ? preview.notes.filter((note): note is string => typeof note === 'string' && note !== '')
        : [],
      email_html: html,
    },
  };
}

/** How many donors the send's answer fixed (`recipient_count`), or null when it carries none. */
export function fixedRecipientCount(body: unknown): number | null {
  if (!isRecord(body) || body.dry_run !== false) return null;
  const count = body.recipient_count;
  return typeof count === 'number' && Number.isInteger(count) ? count : null;
}

/** The guard's classes the organizer fixes by editing the words (`context.class`); `name` is not one. */
const WORD_CLASSES = new Set(['length', 'deductibility', 'payment', 'phone', 'contact', 'link']);

/**
 * Where a refusal of the preview or the send shows (D-FCM-64's decision 4): each where the organizer can
 * act on it, the words kept in the box.
 *
 * - `words`: the guard refused the words themselves (a 422 naming a word class): under the box.
 * - `notice`: the fundraiser's state refused it (every 409: a draft or deactivated fundraiser, no donors
 *   yet, the weekly limit with its `next_send_at`, the 90-day window, Givmo's pause, the charity's terms),
 *   or a name no edit of the words can fix (the guard's `name` class): a notice above the box.
 * - `gone`: messages no longer serve here for this person (the family's route miss, or a refusal of this
 *   person): the page reads the detail again, so the tab follows the server.
 * - `failed`: anything else, a failure that is no refusal: the module's toast.
 */
export type MessageRefusal =
  | { place: 'words'; message: string }
  | { place: 'notice'; message: string; nextSendAt: string | null }
  | { place: 'gone' }
  | { place: 'failed' };

export function messageRefusal(error: unknown): MessageRefusal {
  if (!isGivmoError(error)) return { place: 'failed' };
  if (error.status === 404 || error.status === 403) return { place: 'gone' };
  const code = firstErrorCode(error);
  if (error.status === 422 && code === 'ORGANIZER_MESSAGE_NOT_ALLOWED') {
    const context = firstErrorContext(error);
    const messageClass = typeof context?.class === 'string' ? context.class : '';
    return WORD_CLASSES.has(messageClass)
      ? { place: 'words', message: error.message }
      : { place: 'notice', message: error.message, nextSendAt: null };
  }
  if (error.status === 409) {
    const next =
      code === 'ORGANIZER_MESSAGE_WEEKLY_LIMIT' ? firstErrorContext(error)?.next_send_at : null;
    return {
      place: 'notice',
      message: error.message,
      nextSendAt: typeof next === 'string' && !Number.isNaN(new Date(next).getTime()) ? next : null,
    };
  }
  return { place: 'failed' };
}

/** Whether a refusal is the weekly limit's: a message already went out from this fundraiser this week. */
export function isWeeklyLimit(error: unknown): boolean {
  return (
    isGivmoError(error) &&
    error.status === 409 &&
    firstErrorCode(error) === 'ORGANIZER_MESSAGE_WEEKLY_LIMIT'
  );
}

/** "Oct 22, 2026": a history row's day, in the reader's zone. */
export function historyDay(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? '—'
    : new Intl.DateTimeFormat('en-US', { dateStyle: 'medium' }).format(date);
}

/**
 * "Oct 29, 2026, 2:58 PM": when the next message can go (`next_send_at`), in the reader's zone. The line
 * names a minute, so a moment inside one reads as the next (2:58:29 PM is "2:59 PM"): a Preview pressed
 * during the minute it named would still be refused.
 */
export function nextSendMoment(iso: string): string {
  const minute = 60_000;
  return new Intl.DateTimeFormat('en-US', { dateStyle: 'medium', timeStyle: 'short' }).format(
    new Date(Math.ceil(new Date(iso).getTime() / minute) * minute),
  );
}
